if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._mediaSwitchToken = this._mediaSwitchToken || 0;
      this._announceToken = this._announceToken || 0;
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 768px)');
      this._loopMql = window.matchMedia('(max-width: 767.98px)');
      if (!this.elements.viewer) return;

      // Store handlers so disconnectedCallback can clean them up.
      this._boundSlideChanged = debounce(this.onSlideChanged.bind(this), 150);
      this._thumbClickHandlers = [];
      this.elements.viewer.addEventListener('slideChanged', this._boundSlideChanged);
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (!btn) return;
          const handler = this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false);
          btn.addEventListener('click', handler);
          this._thumbClickHandlers.push({ btn, handler });
        });
      }
      this._boundLoopMediaChange = this._syncMobileLoop.bind(this);
      if (typeof this._loopMql.addEventListener === 'function') {
        this._loopMql.addEventListener('change', this._boundLoopMediaChange);
      } else {
        this._loopMql.addListener(this._boundLoopMediaChange);
      }
      this._waitForLoopIntent();
      if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    disconnectedCallback() {
      /* This element is re-parented during the initial PDP layout pass and
         can be detached/re-grafted during linked-product swaps. Cleanup must
         therefore be paired with a real re-initialisation on reconnect;
         keeping `_initialized = true` left thumbnails permanently inert. */
      this._mediaSwitchToken = (this._mediaSwitchToken || 0) + 1;
      this._announceToken = (this._announceToken || 0) + 1;
      clearTimeout(this._mediaEnterTimer);
      clearTimeout(this._liveRegionTimer);
      clearTimeout(this._loopSettleTimer);
      cancelAnimationFrame(this._loopSetupFrame);
      this.removeEventListener('pointerdown', this._boundLoopIntent);
      this.removeEventListener('keydown', this._boundLoopIntent);
      this.removeEventListener('wheel', this._boundLoopIntent);
      this._boundLoopIntent = null;
      this._loopIntentReady = false;
      if (this._loopMql && this._boundLoopMediaChange) {
        if (typeof this._loopMql.removeEventListener === 'function') {
          this._loopMql.removeEventListener('change', this._boundLoopMediaChange);
        } else {
          this._loopMql.removeListener(this._boundLoopMediaChange);
        }
      }
      this._teardownMobileLoop();
      if (this.elements && this.elements.viewer && this._boundSlideChanged) {
        this.elements.viewer.removeEventListener('slideChanged', this._boundSlideChanged);
      }
      if (this._thumbClickHandlers) {
        this._thumbClickHandlers.forEach(({ btn, handler }) => {
          btn.removeEventListener('click', handler);
        });
        this._thumbClickHandlers = null;
      }
      this._initialized = false;
    }

    // onSlideChanged(event) {
    //   const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
    //   this.setActiveThumbnail(thumbnail);
    // }
    onSlideChanged(event) {
      if (!event.detail || !event.detail.currentElement) {
        return;
      }
      const currentElement = this._loopState
        ? this._closestLoopSlide(true)
        : event.detail.currentElement;
      if (!currentElement) return;
      this.prioritizeActiveImage(currentElement);
      if (!this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    prioritizeActiveImage(media) {
      // Non-primary images stay low priority until the shopper shows one.
      media.querySelectorAll('img').forEach((image) => { image.fetchPriority = 'high'; });
    }

    _waitForLoopIntent() {
      // Boundary copies can make an offscreen image eligible for a download.
      // The real slides and controls already work without those copies. Create
      // continuous-wrap decoration only for a gallery gesture, before its
      // default action, rather than downloading secondary images on hero paint.
      const ready = () => {
        if (!this.isConnected || this._boundLoopIntent !== ready) return;
        this._loopIntentReady = true;
        this.removeEventListener('pointerdown', this._boundLoopIntent);
        this.removeEventListener('keydown', this._boundLoopIntent);
        this.removeEventListener('wheel', this._boundLoopIntent);
        this._syncMobileLoop();
      };
      this._boundLoopIntent = ready;
      this.addEventListener('pointerdown', ready, { passive: true });
      this.addEventListener('keydown', ready);
      this.addEventListener('wheel', ready, { passive: true });
    }

    _syncMobileLoop() {
      if (!this.isConnected || !this._loopMql) return;
      if (this._loopMql.matches && this._loopIntentReady) this._setupMobileLoop();
      else this._teardownMobileLoop();
    }

    _prepareLoopClone(source, edge) {
      const clone = source.cloneNode(true);
      clone.removeAttribute('id');
      clone.dataset.loopClone = edge;
      clone.removeAttribute('data-media-id');
      clone.classList.remove('is-active', 'is-media-entering');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('[aria-controls], [aria-describedby], [aria-labelledby]').forEach((element) => {
        element.removeAttribute('aria-controls');
        element.removeAttribute('aria-describedby');
        element.removeAttribute('aria-labelledby');
      });
      clone.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.fetchPriority = 'low';
        image.decoding = 'async';
      });
      /* A source slide may already have loaded deferred video/model content.
         Boundary clones are visual continuity only: restore their poster and
         never duplicate playback, iframe, or WebGL instances. */
      clone.querySelectorAll('deferred-media[loaded], product-model[loaded]').forEach((media) => {
        media.removeAttribute('loaded');
        Array.from(media.children).forEach((child) => {
          if (child.matches('video, iframe, model-viewer')) child.remove();
        });
      });
      return clone;
    }

    _setupMobileLoop() {
      if (this._loopState || !this.elements || !this.elements.viewer) return;
      const slider = this.elements.viewer.querySelector('.product__media-list');
      if (!slider) return;
      const realSlides = Array.from(slider.children).filter((slide) =>
        slide.matches('.slider__slide[data-media-id]:not([data-loop-clone])')
      );
      if (realSlides.length < 2) return;
      realSlides.forEach((slide, index) => {
        if (slide.dataset.loopLogicalIndex == null) slide.dataset.loopLogicalIndex = String(index);
      });

      const headClone = this._prepareLoopClone(realSlides[realSlides.length - 1], 'head');
      const tailClone = this._prepareLoopClone(realSlides[0], 'tail');
      slider.prepend(headClone);
      slider.append(tailClone);

      const abort = new AbortController();
      const loopState = {
        slider,
        realSlides,
        headClone,
        tailClone,
        abort,
        baseScrollBehavior: slider.style.scrollBehavior,
        baseScrollSnapType: slider.style.scrollSnapType
      };
      this._loopState = loopState;
      const scheduleSettle = () => {
        clearTimeout(this._loopSettleTimer);
        this._loopSettleTimer = setTimeout(() => this._settleMobileLoop(), 120);
      };
      slider.addEventListener('scroll', scheduleSettle, { passive: true, signal: abort.signal });
      if ('onscrollend' in slider) {
        slider.addEventListener('scrollend', () => this._settleMobileLoop(), { signal: abort.signal });
      }

      /* Adding the leading clone changes the visible slide immediately.
         Position the real slide in this same setup frame, before it can paint;
         waiting for another frame briefly exposes a lazy, low-priority clone. */
      const activeSlide = realSlides.find((slide) => slide.classList.contains('is-active')) || realSlides[0];
      this._teleportLoopTo(activeSlide, this._logicalLoopIndex(activeSlide), false);
    }

    _closestLoopSlide(realOnly = false) {
      if (!this._loopState) return null;
      const { slider, realSlides, headClone, tailClone } = this._loopState;
      const candidates = realOnly ? realSlides : [headClone, ...realSlides, tailClone];
      const viewport = slider.getBoundingClientRect();
      const center = viewport.left + viewport.width / 2;
      return candidates.reduce((closest, slide) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        return !closest || distance < closest.distance ? { slide, distance } : closest;
      }, null)?.slide || null;
    }

    _teleportLoopTo(target, logicalIndex, announce = true) {
      if (!this._loopState || !target) return;
      const loopState = this._loopState;
      const { slider } = loopState;
      this._loopTeleporting = true;
      const previousBehavior = slider.style.scrollBehavior;
      const previousSnap = slider.style.scrollSnapType;
      slider.style.scrollBehavior = 'auto';
      slider.style.scrollSnapType = 'none';
      const sliderRect = slider.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const delta = targetRect.left - sliderRect.left - (slider.clientWidth - target.clientWidth) / 2;
      slider.scrollLeft += delta;
      void slider.offsetWidth;
      requestAnimationFrame(() => {
        if (this._loopState !== loopState) return;
        slider.style.scrollBehavior = previousBehavior;
        slider.style.scrollSnapType = previousSnap;
        this._loopTeleporting = false;
        if (announce) {
          this.dispatchEvent(new CustomEvent('pdpLoopSettled', {
            detail: { index: logicalIndex, currentElement: target }
          }));
        }
      });
    }

    _logicalLoopIndex(slide) {
      const index = Number.parseInt(slide?.dataset.loopLogicalIndex, 10);
      return Number.isInteger(index) ? index : 0;
    }

    _settleMobileLoop() {
      if (!this._loopState || this._loopTeleporting) return;
      const { realSlides, headClone, tailClone } = this._loopState;
      const closest = this._closestLoopSlide(false);
      if (closest === tailClone) {
        this._teleportLoopTo(realSlides[0], this._logicalLoopIndex(realSlides[0]));
      } else if (closest === headClone) {
        const lastSlide = realSlides[realSlides.length - 1];
        this._teleportLoopTo(lastSlide, this._logicalLoopIndex(lastSlide));
      }
    }

    _teardownMobileLoop() {
      if (!this._loopState) return;
      const { slider, headClone, tailClone, abort, baseScrollBehavior, baseScrollSnapType } = this._loopState;
      abort.abort();
      clearTimeout(this._loopSettleTimer);
      slider.style.scrollBehavior = baseScrollBehavior;
      slider.style.scrollSnapType = baseScrollSnapType;
      headClone.remove();
      tailClone.remove();
      this._loopState = null;
      this._loopTeleporting = false;
    }
    
    setActiveMedia(mediaId, prepend) {
      if (!this.elements || !this.elements.viewer) return;
      const rebuildLoop = Boolean(prepend && this._loopState);
      if (rebuildLoop) this._teardownMobileLoop();
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) {
        if (rebuildLoop && this._loopMql?.matches) this._setupMobileLoop();
        return; // Guard: mediaId not present in this viewer
      }
      this.prioritizeActiveImage(activeMedia);
      const switchToken = ++this._mediaSwitchToken;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
        element.classList.remove('is-media-entering');
      });
      activeMedia.classList.add('is-active');

      /* CSS animation works in older Safari/WebViews that do not expose the
         Web Animations API. Re-adding the class gives every deliberate media
         change the same soft settle without animating initial page paint. */
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        void activeMedia.offsetWidth;
        activeMedia.classList.add('is-media-entering');
        clearTimeout(this._mediaEnterTimer);
        this._mediaEnterTimer = setTimeout(() => {
          if (switchToken === this._mediaSwitchToken) activeMedia.classList.remove('is-media-entering');
        }, 460);
      }

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail && activeThumbnail.parentElement) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      window.requestAnimationFrame(() => {
        if (switchToken !== this._mediaSwitchToken || !this.isConnected) return;
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (rebuildLoop && this._loopMql?.matches) {
        requestAnimationFrame(() => this._setupMobileLoop());
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      const thumbBtn = thumbnail.querySelector('button');
      if (thumbBtn) thumbBtn.setAttribute('aria-current', true);
      if (typeof this.elements.thumbnails.isSlideVisible === 'function' &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      if (this.elements.thumbnails.slider) {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }
    }

    announceLiveRegion(activeItem, position) {
      if (!this.elements.liveRegion || !window.accessibilityStrings) return;
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      const announceToken = ++this._announceToken;
      const announce = () => {
        if (announceToken !== this._announceToken || !this.isConnected) return;
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        clearTimeout(this._liveRegionTimer);
        this._liveRegionTimer = setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      /* Never reset image.src merely to trigger an announcement: doing so can
         restart responsive image selection and race a rapid thumbnail click. */
      if (image.complete) announce();
      else image.addEventListener('load', announce, { once: true });
    }

    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}

(function () {
  if (window.productGalleryImageMenuBound) return;
  window.productGalleryImageMenuBound = true;
  const imageTargets = [
    'media-gallery .product__media-list img',
    'media-gallery .thumbnail-list img',
    'media-gallery a.pswp-product-image',
    'media-gallery .image-magnify-lens',
    'product-modal .product-media-modal__content img',
    '.pswp--product-gallery .pswp__img',
    '.pswp--product-gallery .pswp__zoom-wrap',
    '.product-image-magnifier'
  ].join(', ');
  function preventImageSaveGesture(event) {
    if (event.target instanceof Element && event.target.closest(imageTargets)) {
      event.preventDefault();
    }
  }
  document.addEventListener('contextmenu', preventImageSaveGesture);
  document.addEventListener('dragstart', preventImageSaveGesture);
})();
